/**
 * Minimal notification pub/sub for write-safety error reporting.
 * Any module can call notify.error("msg") — the <Notifications /> component renders it.
 * Rollback logic must complete BEFORE calling notify. Never let a toast failure block rollback.
 *
 * Part of write-safety fix (Bucket A). See ~/.claude/plans/modular-wiggling-pixel.md
 */

import { useState, useEffect, useRef } from "react";
import { C, font } from "./styles";

let listeners = [];

const notify = {
  error: (msg) => {
    try { listeners.forEach((fn) => fn({ type: "error", msg })); }
    catch (e) { console.error("notify render failed:", e); }
  },
  // Neutral confirmation toast (e.g. "Moved to Trash"). Same pipeline as error,
  // rendered in the text color instead of danger.
  info: (msg) => {
    try { listeners.forEach((fn) => fn({ type: "info", msg })); }
    catch (e) { console.error("notify render failed:", e); }
  },
  subscribe: (fn) => {
    listeners.push(fn);
    return () => { listeners = listeners.filter((l) => l !== fn); };
  },
};

export default notify;

/** Mount once at app root (App.jsx). Renders error toasts in bottom-right corner. */
export function Notifications() {
  const [items, setItems] = useState([]);
  const timers = useRef({});
  const idRef = useRef(0);

  useEffect(() => {
    return notify.subscribe((evt) => {
      const id = ++idRef.current;
      setItems((prev) => [...prev, { id, msg: evt.msg, type: evt.type }]);
      timers.current[id] = setTimeout(() => {
        setItems((prev) => prev.filter((t) => t.id !== id));
        delete timers.current[id];
      }, 5000);
    });
  }, []);

  useEffect(() => {
    const t = timers.current;
    return () => Object.values(t).forEach(clearTimeout);
  }, []);

  if (!items.length) return null;

  return (
    <div style={{
      position: "fixed", bottom: 20, right: 20, zIndex: 9999,
      display: "flex", flexDirection: "column", gap: 8, maxWidth: 360,
    }}>
      {items.map((t) => (
        <div key={t.id} style={{
          padding: "10px 14px", background: C.surface,
          border: `1px solid ${t.type === "info" ? C.border : `${C.danger}40`}`, borderRadius: 6,
          fontSize: 13, fontWeight: 300, color: t.type === "info" ? C.text : C.danger,
          fontFamily: font,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          cursor: "pointer",
        }} onClick={() => { clearTimeout(timers.current[t.id]); delete timers.current[t.id]; setItems((prev) => prev.filter((i) => i.id !== t.id)); }}>
          {t.msg}
        </div>
      ))}
    </div>
  );
}
